/**
 * LogUtil — Frontend structured logger
 *
 * Overrides the global console so every console.log/warn/error/debug call
 * anywhere in the app automatically gets the current trace context prepended:
 *   sessionId, userId, page
 *
 * Setup (index.js — one time):
 *   import LogUtil from './utils/LogUtil';
 *   LogUtil.init();
 *
 * Route tracking (inside <Router> in App.jsx — one time):
 *   LogUtil.setPage(location.pathname);
 *
 * Auth tracking (AuthContext.jsx — on login/logout):
 *   LogUtil.setUserId(user.id);   // on login
 *   LogUtil.setUserId(null);      // on logout
 *
 * After init(), all existing console.log calls get context for free.
 * No imports needed in individual components/pages.
 */

// Save native console methods BEFORE any override to avoid infinite recursion
const _native = {
    log:   console.log.bind(console),
    info:  console.info.bind(console),
    warn:  console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
};

// Shared context — updated at key lifecycle points (init, login, route change)
const _ctx = {
    sessionId: null,
    userId:    null,
    page:      null,
};

// Styles for the context prefix tag in browser DevTools
const _styles = {
    info:  'color:#6b7280;font-size:10px;font-weight:bold',
    warn:  'color:#d97706;font-size:10px;font-weight:bold',
    error: 'color:#dc2626;font-size:10px;font-weight:bold',
    debug: 'color:#8b5cf6;font-size:10px;font-weight:bold',
};

function _buildPrefix() {
    const page      = _ctx.page      || '-';
    const userId    = _ctx.userId    != null ? `u:${_ctx.userId}` : 'u:?';
    const sessionId = _ctx.sessionId ? `s:${_ctx.sessionId.slice(-6)}` : 's:?';
    return `[${page} | ${userId} | ${sessionId}]`;
}

function _overrideConsole() {
    const levelMap = { log: 'info', info: 'info', warn: 'warn', error: 'error', debug: 'debug' };

    for (const [method, level] of Object.entries(levelMap)) {
        console[method] = function (...args) {
            const prefix = _buildPrefix();
            const style  = _styles[level] || _styles.info;
            try {
                _native[method](`%c${prefix}%c`, style, '', ...args);
            } catch (_) {
                // Swallow WebSocket send errors when the dev server is disconnected
            }
        };
    }
}

const LogUtil = {

    /**
     * Call once at app startup (index.js).
     * Generates/restores a sessionId and overrides console.
     */
    init() {
        // Persist sessionId for the browser session
        let sessionId = sessionStorage.getItem('_log_session_id');
        if (!sessionId) {
            sessionId = typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                    const r = Math.random() * 16 | 0;
                    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                });
            sessionStorage.setItem('_log_session_id', sessionId);
        }
        _ctx.sessionId = sessionId;
        _overrideConsole();
    },

    /** Call on route change (useLocation inside <Router>) */
    setPage(pathname) {
        _ctx.page = pathname;
    },

    /** Call after login; pass null on logout */
    setUserId(userId) {
        _ctx.userId = userId;
    },

    /** Returns a snapshot of the current trace context */
    getContext() {
        return { ..._ctx };
    },
};

export default LogUtil;
